import { TaskStatus } from "@/src/models/task";

/** Where a task's workflow run stands, as its status says. */
export enum TaskRunState {
  /** No run has started (backlog), or the task is blocked outside the workflow. */
  NOT_STARTED = "not_started",
  /** A run is in progress: executing, or waiting on a timer it will wake from. */
  GOING = "going",
  /** The run stopped for ops, who can act on it: it waits for information or review, or it failed. */
  HELD_FOR_OPS = "held_for_ops",
  /** The run finished: the task awaits verification or is completed. */
  FINISHED = "finished",
  /** The task was closed outside the run: cancelled, a duplicate, not relevant, reverted or hidden. */
  CLOSED = "closed",
}

/** The one place that sorts task statuses for the workflow; adding a status makes TypeScript ask where it goes. */
export function taskRunState(status: TaskStatus): TaskRunState {
  switch (status) {
    case TaskStatus.BACKLOG:
    case TaskStatus.BLOCKED:
      return TaskRunState.NOT_STARTED;
    case TaskStatus.PROCESSING:
    case TaskStatus.PAUSED:
      return TaskRunState.GOING;
    case TaskStatus.AWAITING_INFORMATION:
    case TaskStatus.REVIEW_NEEDED:
    case TaskStatus.FAILED:
      return TaskRunState.HELD_FOR_OPS;
    case TaskStatus.VERIFICATION_NEEDED:
    case TaskStatus.COMPLETED:
      return TaskRunState.FINISHED;
    case TaskStatus.CANCELLED:
    case TaskStatus.DUPLICATE:
    case TaskStatus.NOT_RELEVANT:
    case TaskStatus.REVERT:
    case TaskStatus.SHADOW:
      return TaskRunState.CLOSED;
  }
}

/** A run is in progress, so nothing but the engine should change the task's rows. */
export function isRunGoing(status: TaskStatus): boolean {
  return taskRunState(status) === TaskRunState.GOING;
}

/** The run stopped for ops, who can act on it. */
export function isRunHeldForOps(status: TaskStatus): boolean {
  return taskRunState(status) === TaskRunState.HELD_FOR_OPS;
}

/** The run stopped for ops without failing: it waits for information or review. */
export function isRunParkedForOps(status: TaskStatus): boolean {
  return isRunHeldForOps(status) && status !== TaskStatus.FAILED;
}

/** The run is in progress or held for ops, so steps that haven't run yet may still run. */
export function isRunOpen(status: TaskStatus): boolean {
  return isRunGoing(status) || isRunHeldForOps(status);
}
